import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Bell,
  BookOpen,
  Bot,
  Building2,
  CalendarDays,
  Car,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  DoorOpen,
  FileText,
  Headphones,
  Home,
  Landmark,
  Menu,
  MessageSquareText,
  ParkingCircle,
  Receipt,
  ReceiptIndianRupee,
  ShieldCheck,
  Sparkles,
  TreePalm,
  UserRound,
  Users,
  Wrench,
  X,
} from "lucide-react";

import { Seo, SITE_URL } from "../components/Seo";
import PublicAskExperience from "../components/PublicAskExperience";
import { Button } from "../components/ui/button";
import PricingSection from "../components/landing/PricingSection";
import {
  FloatingWhatsAppControl,
  LandingContactSection,
} from "../components/landing/LandingContactSection";
import { LANDING_CONTACT } from "../config/landingPricing";

const HERO_IMAGE =
  "https://images.pexels.com/photos/9497543/pexels-photo-9497543.jpeg?auto=compress&cs=tinysrgb&w=1600";
const RESIDENT_IMAGE =
  "https://images.pexels.com/photos/4259140/pexels-photo-4259140.jpeg?auto=compress&cs=tinysrgb&w=1200";

const NAV_ITEMS = [
  ["Home", "top"],
  ["Ask Society Mitra", "ask-your-society-mitra"],
  ["Society Management", "society-management"],
  ["Pricing", "pricing"],
  ["Residents", "resident-portal"],
  ["Services", "services"],
  ["Vendors", "vendors"],
  ["Experts", "experts"],
  ["Knowledge", "knowledge"],
  ["Contact", "contact"],
];

const TRIAL_LABEL = "Start with a 30-Day Trial";

const FAQS = [
  [
    "What is MySocietyMitra?",
    "MySocietyMitra is a connected Housing Society platform covering Society management (ERP), a Resident Portal, Society services, a verified vendor network, Expert consultations and Ask Your Society Mitra — AI-powered informational guidance for everyday Society questions.",
  ],
  [
    "What is Ask Your Society Mitra?",
    "Ask Your Society Mitra lets anyone type a Housing Society question — about meetings, notices, maintenance, records or complaints — and receive instant AI-generated informational guidance. It is not a replacement for qualified legal, financial, structural or other professional advice.",
  ],
  [
    "Is there a trial for Societies?",
    "Yes. Societies receive a 30-day trial of the Society management platform after onboarding activation.",
  ],
  [
    "Can vendors and Experts join?",
    "Yes. Vendors can build a verified profile with service categories, service areas and documents. Experts can set up a professional profile, availability and consultation workflow with Housing Societies.",
  ],
];

const QUICK_VALUES = [
  ["Society Management", Building2, "bg-blue-50 text-blue-700"],
  ["Resident Portal", Users, "bg-emerald-50 text-emerald-700"],
  ["Society Services", Wrench, "bg-orange-50 text-orange-700"],
  ["Expert Guidance", Headphones, "bg-violet-50 text-violet-700"],
  ["AI Guidance", Bot, "bg-rose-50 text-rose-700"],
];

const SOCIETY_MODULES = [
  ["Members & Units", Users, "blue"],
  ["Maintenance Billing", ReceiptIndianRupee, "orange"],
  ["Collections & Receipts", ReceiptIndianRupee, "green"],
  ["Society Accounting", FileText, "violet"],
  ["Complaints & Helpdesk", MessageSquareText, "rose"],
  ["Notices & Documents", Bell, "blue"],
  ["Meetings & Requests", CalendarDays, "green"],
  ["Assets & AMC", ClipboardList, "orange"],
  ["Gate, Amenities & Parking", ParkingCircle, "violet"],
  ["Staff & Operations", ShieldCheck, "rose"],
];

const RESIDENT_FEATURES = [
  ["My Society", Building2, "bg-blue-100 text-blue-700"],
  ["My Units", Home, "bg-emerald-100 text-emerald-700"],
  ["Household", Users, "bg-orange-100 text-orange-700"],
  ["Bills", ReceiptIndianRupee, "bg-violet-100 text-violet-700"],
  ["Receipts", Receipt, "bg-rose-100 text-rose-700"],
  ["Ledger", Landmark, "bg-blue-100 text-blue-700"],
  ["Complaints", MessageSquareText, "bg-emerald-100 text-emerald-700"],
  ["Notices", Bell, "bg-orange-100 text-orange-700"],
  ["Meetings", CalendarDays, "bg-violet-100 text-violet-700"],
  ["Documents", FileText, "bg-rose-100 text-rose-700"],
  ["Formal Requests", ClipboardList, "bg-blue-100 text-blue-700"],
  ["Visitor Approvals", DoorOpen, "bg-emerald-100 text-emerald-700"],
  ["Amenities", TreePalm, "bg-orange-100 text-orange-700"],
  ["Parking", Car, "bg-violet-100 text-violet-700"],
  ["Profile", UserRound, "bg-rose-100 text-rose-700"],
];

const SERVICE_CATEGORIES = [
  "CCTV",
  "Intercom / Video Door",
  "Security",
  "Solar",
  "Auto Pump",
  "Structural Audit",
  "Building Painting",
  "STP Maintenance",
  "Garden Maintenance",
  "Fire Safety",
  "DG / Generator",
  "Roof Shed",
  "Pest Control",
  "Electrical",
  "Carpentry",
  "Plumbing",
  "Boom Barrier",
  "Civil Work",
  "Lift",
  "Housekeeping",
  "Water Tank Cleaning",
  "Waterproofing",
];

const BENEFITS = [
  [
    "Save Time & Effort",
    "Bring common Society work into one organized workspace.",
    "blue",
  ],
  [
    "Reduce Manual Work",
    "Move beyond scattered follow-ups and untracked records.",
    "orange",
  ],
  [
    "Organized Records",
    "Keep documents, notices, requests and activity easier to find.",
    "green",
  ],
  [
    "Better Communication",
    "Help committees and residents stay on the same page.",
    "violet",
  ],
  [
    "Role-Based Access",
    "Give the right people access to the right operations.",
    "blue",
  ],
  [
    "One Connected Ecosystem",
    "Connect Society operations, services, vendors and Experts.",
    "rose",
  ],
];

const WORKFLOW = [
  ["Society", Building2],
  ["Manage", ClipboardList],
  ["Connect Residents", Users],
  ["Access Services / Experts", Wrench],
  ["Track & Organize", BadgeCheck],
  ["Grow Together", Sparkles],
];

export default function PremiumLanding() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const register = (type = "society", applicantType = "") => {
    try {
      sessionStorage.setItem("msm_onboard_type", type);
      if (applicantType) {
        sessionStorage.setItem("msm_vendor_applicant_type", applicantType);
      } else {
        sessionStorage.removeItem("msm_vendor_applicant_type");
      }
    } catch {
      // Storage is optional for navigation.
    }
    navigate(type === "vendor" || type === "expert" ? "/join-provider" : "/login");
  };

  return (
    <div className="overflow-x-hidden bg-slate-50 text-slate-900" id="top">
      <Seo
        description="MySocietyMitra — Housing Society management software with Society ERP, Resident Portal, Housing Society services, verified Society vendors, Society Experts and Ask Your Society Mitra for AI-powered Housing Society guidance."
        image={`${SITE_URL}/mysocietymitra-official-logo.png`}
        path="/"
        title="MySocietyMitra — Housing Society Management Software, Resident Portal & Ask Your Society Mitra"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "MySocietyMitra",
          alternateName: "Apka Society Mitra",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: SITE_URL,
          image: `${SITE_URL}/mysocietymitra-official-logo.png`,
          description:
            "Housing Society management software (Society ERP) connecting Society operations, a Resident Portal, Housing Society services, Society vendors, Society Experts and Ask Your Society Mitra AI guidance.",
          featureList: [
            "Society ERP",
            "Resident Portal",
            "Housing Society Services",
            "Society Vendors",
            "Society Experts",
            "Ask Your Society Mitra",
          ],
          contactPoint: LANDING_CONTACT.phones.map((phone) => ({
            "@type": "ContactPoint",
            contactType: "customer support",
            telephone: phone.href.replace("tel:", ""),
          })),
          publisher: { "@id": `${SITE_URL}/#org` },
        }}
      />

      <AnnouncementBar onRegister={() => register("society")} />
      <PremiumNavigation
        menuOpen={menuOpen}
        onKnowledge={() => navigate("/knowledge")}
        onLogin={() => navigate("/login")}
        onRegister={() => register("society")}
        onScroll={scrollTo}
        scrolled={scrolled}
        setMenuOpen={setMenuOpen}
      />

      <Hero
        onExplore={() => scrollTo("society-management")}
        onRegister={() => register("society")}
        onAsk={() => scrollTo("ask-your-society-mitra")}
      />
      <PublicAskExperience onRegister={() => register("society")} />
      <QuickValueStrip />
      <ProblemSolution />
      <SocietyManagement onRegister={() => register("society")} />
      <PricingSection
        onExpert={() => register("expert")}
        onTechnician={() => register("vendor", "TECHNICIAN")}
        onVendor={() => register("vendor")}
      />
      <ResidentPortal onRegister={() => register("society")} />
      <Services onRegister={() => register("society")} />
      <Workflow />
      <WhyMitra />
      <Faq />
      <FinalCta
        onAsk={() => scrollTo("ask-your-society-mitra")}
        onExpert={() => register("expert")}
        onRegister={() => register("society")}
        onVendor={() => register("vendor")}
      />
      <LandingContactSection />
      <PremiumFooter
        onKnowledge={() => navigate("/knowledge")}
        onLogin={() => navigate("/login")}
        onRegister={() => register("society")}
      />
      <FloatingWhatsAppControl />
    </div>
  );
}

function AnnouncementBar({ onRegister }) {
  return (
    <div
      className="bg-blue-950 px-5 py-2.5 text-center text-xs text-blue-100 sm:text-sm"
      data-testid="premium-announcement"
    >
      <span className="font-semibold text-white">Apka Society Mitra</span>
      <span className="mx-2 text-orange-300">•</span>
      <span data-testid="premium-trial-label">{TRIAL_LABEL}</span>
      <span className="mx-2 hidden text-blue-200 sm:inline">•</span>
      <span className="hidden text-blue-100 sm:inline">Call:</span>
      {LANDING_CONTACT.phones.map((phone, index) => (
        <span className="hidden sm:inline" key={phone.href}>
          {index > 0 && <span className="mx-1 text-blue-300">|</span>}
          <a
            className="font-semibold text-white underline underline-offset-2 hover:text-orange-200"
            data-testid={`premium-contact-strip-${index + 1}`}
            href={phone.href}
          >
            {phone.label}
          </a>
        </span>
      ))}
      <button
        className="ml-3 font-bold text-orange-300 underline underline-offset-4"
        data-testid="premium-announcement-register"
        onClick={onRegister}
        type="button"
      >
        Get started
      </button>
    </div>
  );
}

function PremiumNavigation({
  menuOpen,
  onKnowledge,
  onLogin,
  onRegister,
  onScroll,
  scrolled,
  setMenuOpen,
}) {
  const landingTarget = (target) => {
    if (target === "vendors" || target === "experts") {
      return "pricing";
    }
    return target;
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-[background-color,box-shadow] duration-300 ${scrolled ? "border-slate-200/70 bg-white/90 shadow-sm backdrop-blur-xl" : "border-transparent bg-white"}`}
    >
      <div className="mx-auto flex h-[84px] max-w-7xl items-center justify-between gap-4 px-5 lg:px-8">
        <button
          className="flex min-w-0 items-center"
          data-testid="premium-logo-home"
          onClick={() => onScroll("top")}
          type="button"
        >
          <img
            alt="MySocietyMitra by Shivkrupa Enterprises — Apka Society Mitra official logo"
            className="h-16 w-auto object-contain sm:h-[72px]"
            data-testid="premium-logo-image"
            src="/mysocietymitra-official-logo.png"
          />
        </button>
        <nav
          className="hidden items-center gap-5 xl:flex"
          data-testid="premium-navigation"
        >
          {NAV_ITEMS.map(([label, target]) => (
            <button
              className="text-sm font-semibold text-slate-600 transition-colors hover:text-blue-900"
              data-testid={`premium-nav-${target}`}
              key={target}
              onClick={() =>
                label === "Knowledge" ? onKnowledge() : onScroll(landingTarget(target))
              }
              type="button"
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <Button
            className="rounded-full text-slate-700 hover:bg-slate-100"
            data-testid="premium-nav-login"
            onClick={onLogin}
            type="button"
            variant="ghost"
          >
            Login
          </Button>
          <Button
            className="rounded-full bg-orange-500 px-5 text-white hover:bg-orange-600"
            data-testid="premium-nav-register"
            onClick={onRegister}
            type="button"
          >
            Get Started <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <button
          aria-label="Toggle navigation menu"
          className="rounded-lg p-2 text-blue-950 lg:hidden"
          data-testid="premium-nav-menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          type="button"
        >
          {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {menuOpen && (
        <div
          className="border-t border-slate-200 bg-white px-5 py-5 shadow-lg lg:hidden"
          data-testid="premium-mobile-navigation"
        >
          <div className="grid gap-1">
            {NAV_ITEMS.map(([label, target]) => (
              <button
                className="rounded-lg px-3 py-3 text-left text-sm font-semibold text-slate-700 hover:bg-blue-50"
                data-testid={`premium-mobile-nav-${target}`}
                key={target}
                onClick={() =>
                  label === "Knowledge" ? onKnowledge() : onScroll(landingTarget(target))
                }
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button
              data-testid="premium-mobile-login"
              onClick={onLogin}
              type="button"
              variant="outline"
            >
              Login
            </Button>
            <Button
              className="bg-orange-500 text-white hover:bg-orange-600"
              data-testid="premium-mobile-register"
              onClick={onRegister}
              type="button"
            >
              Get Started
            </Button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <a
              className="flex h-10 items-center justify-center border border-blue-200 text-sm font-bold text-blue-900"
              data-testid="premium-mobile-contact-call"
              href={LANDING_CONTACT.phones[0].href}
            >
              Call us
            </a>
            <a
              className="flex h-10 items-center justify-center bg-emerald-600 text-sm font-bold text-white"
              data-testid="premium-mobile-contact-whatsapp"
              href={LANDING_CONTACT.whatsappHref}
              rel="noreferrer"
              target="_blank"
            >
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero({ onAsk, onExplore, onRegister }) {
  return (
    <section
      className="relative isolate overflow-hidden bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-900"
      data-testid="premium-hero"
    >
      <img
        alt="Residential Housing Society apartment buildings"
        className="absolute inset-0 -z-20 h-full w-full object-cover opacity-30"
        fetchPriority="high"
        loading="eager"
        src={HERO_IMAGE}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-blue-950 via-blue-950/85 to-indigo-950/55" />
      <div className="absolute -right-20 top-16 -z-10 h-80 w-80 rounded-full bg-orange-500/30 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 -z-10 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="mx-auto grid min-h-[680px] max-w-7xl gap-12 px-5 py-16 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:px-8 lg:py-24">
        <div className="animate-fade-up text-white">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-bold tracking-[0.14em] text-blue-100">
            <Sparkles className="h-4 w-4 text-orange-300" />
            MYSOCIETYMITRA BY SHIVKRUPA ENTERPRISES
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-5xl font-black leading-[0.95] text-white sm:text-6xl lg:text-7xl">
            ONE PLATFORM.
            <span className="block text-orange-300">AN ENTIRE SOCIETY</span>
            ECOSYSTEM.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-blue-100 sm:text-lg">
            Housing Society management software with a Resident Portal, Society
            services, verified vendors, Experts and{" "}
            <strong className="font-semibold text-white">
              Ask Your Society Mitra
            </strong>{" "}
            — AI-powered Society guidance, all from one platform.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              className="h-12 rounded-full bg-orange-500 px-6 text-base text-white shadow-lg shadow-orange-500/30 hover:bg-orange-600"
              data-testid="premium-hero-get-started"
              onClick={onRegister}
              type="button"
            >
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <button
              className="inline-flex h-12 items-center gap-2 rounded-full px-5 text-sm font-semibold text-blue-100 transition-colors hover:bg-white/10 hover:text-white"
              data-testid="premium-hero-explore"
              onClick={onExplore}
              type="button"
            >
              Explore Society ERP <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div
            className="mt-6 inline-flex max-w-full flex-col gap-2 rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur sm:flex-row sm:items-center sm:gap-4 sm:pl-4"
            data-testid="premium-hero-ask-card"
          >
            <span className="text-sm font-semibold text-blue-50">
              Have a Society Question?
            </span>
            <Button
              className="h-10 rounded-full border-orange-300/50 bg-orange-500/20 px-4 text-sm text-white hover:bg-orange-500/35"
              data-testid="premium-hero-ask"
              onClick={onAsk}
              type="button"
              variant="outline"
            >
              <Sparkles className="mr-2 h-4 w-4 text-orange-300" /> Ask Society
              Mitra
            </Button>
          </div>
          <div className="mt-9 flex flex-wrap gap-x-5 gap-y-3 text-sm text-blue-100">
            {[
              "Manage Society",
              "Connect Residents",
              "Access Services",
              "Consult Experts",
            ].map((item) => (
              <span className="inline-flex items-center gap-2" key={item}>
                <Check className="h-4 w-4 text-emerald-300" />
                {item}
              </span>
            ))}
          </div>
        </div>
        <HeroDevicePresentation />
      </div>
    </section>
  );
}

function HeroDevicePresentation() {
  const labels = [
    ["Society Management", "left-0 top-12"],
    ["Billing & Collections", "right-0 top-3"],
    ["Ask Society Mitra", "-left-2 bottom-20"],
    ["Resident Portal", "right-2 bottom-6"],
  ];
  return (
    <div
      className="relative mx-auto w-full max-w-2xl pb-8"
      data-testid="premium-hero-devices"
    >
      <div className="absolute inset-x-12 bottom-0 h-20 rounded-full bg-blue-950/60 blur-2xl" />
      {labels.map(([label, position], index) => (
        <div
          className={`absolute z-20 hidden rounded-full border border-white/40 bg-white/90 px-3 py-2 text-xs font-bold text-blue-950 shadow-lg sm:flex sm:items-center sm:gap-2 ${position}`}
          key={label}
        >
          <span
            className={`h-2 w-2 rounded-full ${index % 2 ? "bg-orange-500" : "bg-emerald-500"}`}
          />
          {label}
        </div>
      ))}
      <div className="relative ml-0 overflow-hidden border-[7px] border-slate-800 bg-white shadow-2xl sm:ml-8">
        <div className="flex h-10 items-center gap-2 bg-slate-950 px-4 text-xs text-white">
          <span className="h-2 w-2 rounded-full bg-orange-400" />
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
          <span className="ml-2 text-slate-300">MySocietyMitra Dashboard</span>
        </div>
        <div className="grid min-h-[330px] grid-cols-[132px_1fr] bg-slate-50 sm:min-h-[390px]">
          <aside className="hidden bg-blue-950 p-4 text-xs text-blue-100 sm:block">
            <img
              alt="MySocietyMitra official logo"
              className="mb-6 h-14 w-14 rounded-lg bg-white object-contain p-0.5"
              src="/mysocietymitra-official-logo.png"
            />
            {[
              "Dashboard",
              "Members",
              "Billing",
              "Complaints",
              "Notices",
              "Meetings",
              "Services",
              "Experts",
            ].map((item, index) => (
              <div
                className={`mb-3 rounded-md px-2 py-1.5 ${index === 0 ? "bg-white/15 text-white" : ""}`}
                key={item}
              >
                {item}
              </div>
            ))}
          </aside>
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Good morning</p>
                <h2 className="font-display text-xl font-bold text-blue-950 sm:text-2xl">
                  Society overview
                </h2>
              </div>
              <div className="h-9 w-9 rounded-full bg-orange-100" />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["128", "Members", "bg-blue-500"],
                ["₹ 86k", "Collected", "bg-emerald-500"],
                ["12", "Complaints", "bg-orange-500"],
                ["25", "Services", "bg-violet-500"],
              ].map(([value, label, color]) => (
                <div
                  className="border border-slate-100 bg-white p-3 shadow-sm"
                  key={label}
                >
                  <span className={`block h-1 w-8 ${color}`} />
                  <strong className="mt-3 block text-sm text-slate-900">
                    {value}
                  </strong>
                  <span className="text-[10px] text-slate-500">{label}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-[1.4fr_.8fr]">
              <div className="border border-slate-100 bg-white p-4">
                <div className="flex items-end gap-2">
                  <div className="h-16 w-3 bg-blue-200" />
                  <div className="h-24 w-3 bg-blue-500" />
                  <div className="h-12 w-3 bg-orange-400" />
                  <div className="h-20 w-3 bg-emerald-400" />
                  <div className="h-28 w-3 bg-violet-400" />
                </div>
                <p className="mt-3 text-xs font-semibold text-slate-600">
                  Maintenance collection
                </p>
              </div>
              <div className="border border-slate-100 bg-white p-4">
                <div className="mx-auto h-20 w-20 rounded-full border-[12px] border-blue-100 border-t-orange-400 border-r-emerald-400" />
                <p className="mt-3 text-center text-xs font-semibold text-slate-600">
                  Activity status
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -bottom-7 right-3 z-10 w-28 overflow-hidden rounded-[24px] border-[5px] border-slate-900 bg-white shadow-2xl sm:w-36">
        <div className="bg-blue-950 px-3 py-3 text-center text-[9px] font-bold text-white">
          Resident Portal
        </div>
        <div className="grid grid-cols-2 gap-2 p-3">
          {[
            "Bills",
            "Receipts",
            "Notices",
            "Meetings",
            "Amenities",
            "Parking",
          ].map((item) => (
            <div
              className="bg-blue-50 p-2 text-center text-[8px] font-semibold text-blue-900"
              key={item}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickValueStrip() {
  return (
    <section className="relative z-20 -mt-1 border-y border-slate-200 bg-white shadow-sm">
      <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-slate-100 px-5 sm:grid-cols-5 sm:divide-x sm:divide-y-0 lg:px-8">
        {QUICK_VALUES.map(([label, Icon, color]) => (
          <div className="flex items-center gap-3 px-4 py-5" key={label}>
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="text-sm font-bold text-slate-700">{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProblemSolution() {
  const problems = [
    "Scattered records",
    "Manual follow-ups",
    "Untracked complaints",
    "Disconnected communication",
    "Difficult document management",
    "Multiple service contacts",
  ];
  return (
    <section className="scroll-mt-24 py-20 sm:py-28" id="problem-solution">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-2 lg:px-8">
        <div>
          <p className="text-xs font-bold tracking-[0.16em] text-orange-600">
            A BETTER WAY TO ORGANIZE
          </p>
          <h2 className="mt-4 font-display text-4xl font-black leading-tight text-blue-950 sm:text-5xl">
            Still Managing Your Society Through{" "}
            <span className="text-orange-500">
              WhatsApp, Excel & Paper Records?
            </span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">
            Those familiar tools are helpful, but important Society work
            deserves one organized, shared system that is easier to follow.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {problems.map((problem, index) => (
            <article
              className="border border-slate-200 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              key={problem}
            >
              <span className="text-xs font-bold text-slate-400">
                0{index + 1}
              </span>
              <h3 className="mt-4 text-sm font-bold text-slate-800">
                {problem}
              </h3>
            </article>
          ))}
          <article className="col-span-2 bg-gradient-to-r from-blue-900 to-indigo-900 p-6 text-white">
            <span className="text-xs font-bold tracking-[0.14em] text-orange-300">
              ONE DIGITAL ECOSYSTEM
            </span>
            <h3 className="mt-3 font-display text-2xl font-black">
              One Society. One Digital Ecosystem. MySocietyMitra.
            </h3>
          </article>
        </div>
      </div>
    </section>
  );
}

function SocietyManagement({ onRegister }) {
  return (
    <section
      className="scroll-mt-24 bg-white py-20 sm:py-28"
      id="society-management"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.16em] text-blue-700">
            SOCIETY ERP
          </p>
          <h2 className="mt-4 font-display text-4xl font-black text-blue-950 sm:text-5xl">
            Everything Your Society Needs to Stay Organized
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Real Society operations, thoughtfully brought together in one
            connected workspace.
          </p>
        </div>
        <div className="mt-10 grid gap-5 lg:grid-cols-[1.15fr_.85fr]">
          <SocietyDashboard />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {SOCIETY_MODULES.map(([name, Icon, color]) => (
              <ModuleCard color={color} icon={Icon} key={name} name={name} />
            ))}
          </div>
        </div>
        <Button
          className="mt-8 h-12 rounded-full bg-blue-900 px-6 text-white hover:bg-blue-800"
          data-testid="premium-society-cta"
          onClick={onRegister}
          type="button"
        >
          Explore Society Management <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}

function SocietyDashboard() {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-950 to-blue-800 p-6 text-white shadow-xl sm:p-8">
      <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-orange-400/30 blur-3xl" />
      <div className="relative">
        <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-bold tracking-[0.12em] text-blue-100">
          SOCIETY COMMAND CENTRE
        </span>
        <h3 className="mt-5 font-display text-3xl font-black">
          Your daily operations, clearly in view.
        </h3>
        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          {[
            ["Active", "Notices"],
            ["Organized", "Documents"],
            ["Ready", "Requests"],
          ].map(([title, label]) => (
            <div
              className="border border-white/15 bg-white/10 p-4 backdrop-blur"
              key={label}
            >
              <strong className="block font-display text-lg">{title}</strong>
              <span className="text-xs text-blue-100">{label}</span>
            </div>
          ))}
        </div>
        <div className="mt-5 border border-white/15 bg-white p-4 text-slate-700">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-blue-900">
              TODAY'S OVERVIEW
            </span>
            <span className="text-xs text-slate-400">Live workspace</span>
          </div>
          {[
            "Maintenance follow-up",
            "Notice published",
            "Helpdesk request",
            "AMC reminder",
          ].map((item, index) => (
            <div
              className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-sm"
              key={item}
            >
              <span>{item}</span>
              <span
                className={`h-2.5 w-2.5 rounded-full ${index % 2 ? "bg-emerald-500" : "bg-orange-400"}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModuleCard({ color, icon: Icon, name }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700",
    orange: "bg-orange-50 text-orange-700",
    green: "bg-emerald-50 text-emerald-700",
    violet: "bg-violet-50 text-violet-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <article className="group border border-slate-200 bg-slate-50 p-4 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-lg">
      <span
        className={`flex h-10 w-10 items-center justify-center rounded-xl ${colors[color]}`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="mt-4 text-sm font-bold leading-snug text-slate-800">
        {name}
      </h3>
    </article>
  );
}

function ResidentPortal({ onRegister }) {
  return (
    <section
      className="scroll-mt-24 overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-20 sm:py-28"
      data-testid="premium-resident-section"
      id="resident-portal"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-[1fr_1fr] lg:px-8">
        <div>
          <p className="text-xs font-bold tracking-[0.16em] text-emerald-700">
            RESIDENT PORTAL
          </p>
          <h2 className="mt-4 font-display text-4xl font-black text-blue-950 sm:text-5xl">
            Give Residents Their Own Digital Society Portal
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Society information and everyday services at their fingertips.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {RESIDENT_FEATURES.map(([feature, Icon, color]) => (
              <div
                className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm"
                key={feature}
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${color}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                {feature}
              </div>
            ))}
          </div>
          <Button
            className="mt-8 h-12 rounded-full bg-emerald-600 px-6 text-white hover:bg-emerald-700"
            data-testid="premium-resident-cta"
            onClick={onRegister}
            type="button"
          >
            Explore Resident Portal <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <ResidentDevice />
      </div>
    </section>
  );
}

function ResidentDevice() {
  return (
    <div
      className="relative mx-auto w-full max-w-xl"
      data-testid="premium-resident-device"
    >
      <img
        alt="Residents of a Housing Society using the MySocietyMitra Resident Portal"
        className="h-[380px] w-full rounded-[32px] object-cover shadow-xl sm:h-[460px]"
        loading="lazy"
        src={RESIDENT_IMAGE}
      />
      <div className="absolute -bottom-6 left-4 w-[196px] overflow-hidden rounded-[30px] border-[6px] border-slate-900 bg-white shadow-2xl sm:left-8 sm:w-[220px]">
        <div className="bg-gradient-to-r from-emerald-600 to-blue-800 px-4 pb-4 pt-5 text-white">
          <div className="mx-auto mb-3 h-1.5 w-14 rounded-full bg-white/40" />
          <p className="text-[9px] text-emerald-100">Welcome back</p>
          <p className="font-display text-sm font-bold">Resident Portal</p>
          <div className="mt-3 rounded-xl bg-white/15 px-3 py-2 text-[9px]">
            <span className="block text-emerald-100">Current bill</span>
            <strong className="text-xs">₹ 3,250 • Due 10 Jul</strong>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1.5 p-3">
          {RESIDENT_FEATURES.slice(0, 12).map(([feature, Icon, color]) => (
            <div
              className="flex flex-col items-center gap-1 rounded-lg bg-slate-50 px-1 py-2 text-center text-[7.5px] font-bold leading-tight text-slate-700"
              key={feature}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-md ${color}`}
              >
                <Icon className="h-3 w-3" />
              </span>
              {feature}
            </div>
          ))}
        </div>
      </div>
      <div className="absolute right-3 top-6 hidden w-44 rounded-2xl border border-white/70 bg-white/95 p-3 shadow-xl sm:block">
        <div className="flex items-center gap-2 text-[10px] font-bold text-blue-950">
          <Bell className="h-3.5 w-3.5 text-orange-500" /> New notice published
        </div>
        <p className="mt-1 text-[10px] text-slate-500">
          Water tank cleaning — Sunday 7 AM
        </p>
      </div>
      <div className="absolute bottom-24 right-3 hidden w-44 rounded-2xl border border-white/70 bg-white/95 p-3 shadow-xl sm:block">
        <div className="flex items-center gap-2 text-[10px] font-bold text-blue-950">
          <DoorOpen className="h-3.5 w-3.5 text-emerald-600" /> Visitor approval
        </div>
        <p className="mt-1 text-[10px] text-slate-500">
          Courier at gate — Approve / Decline
        </p>
      </div>
    </div>
  );
}

function Services({ onRegister }) {
  return (
    <section className="scroll-mt-24 bg-white py-20 sm:py-28" id="services">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.16em] text-orange-600">
              SOCIETY SERVICES
            </p>
            <h2 className="mt-4 font-display text-4xl font-black text-blue-950 sm:text-5xl">
              Society Services.{" "}
              <span className="text-orange-500">Simplified.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Discover the service categories your Society may need, supported
              through direct, partner, Expert or hybrid fulfilment models.
            </p>
          </div>
          <Button
            className="rounded-full border-blue-200 text-blue-900 hover:bg-blue-50"
            data-testid="premium-services-cta"
            onClick={onRegister}
            type="button"
            variant="outline"
          >
            Explore Services <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {SERVICE_CATEGORIES.map((service, index) => (
            <article
              className="group border border-slate-200 bg-slate-50 p-4 transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-lg"
              key={service}
            >
              <span
                className={`flex h-9 w-9 items-center justify-center rounded-lg ${index % 3 === 0 ? "bg-blue-100 text-blue-700" : index % 3 === 1 ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"}`}
              >
                <Wrench className="h-4 w-4" />
              </span>
              <h3 className="mt-3 text-sm font-bold leading-snug text-slate-700">
                {service}
              </h3>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Workflow() {
  return (
    <section
      className="scroll-mt-24 bg-blue-950 py-20 text-white sm:py-28"
      id="how-it-works"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="text-center">
          <p className="text-xs font-bold tracking-[0.16em] text-orange-300">
            HOW MYSOCIETYMITRA WORKS
          </p>
          <h2 className="mt-4 font-display text-4xl font-black sm:text-5xl">
            From requirement to completion — in one place.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-blue-100">
            An ecosystem for managing daily Society work, connecting people and
            organizing the next step.
          </p>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {WORKFLOW.map(([label, Icon], index) => (
            <div
              className="relative border border-white/15 bg-white/10 p-5 backdrop-blur"
              key={label}
            >
              <span className="text-xs font-bold text-orange-300">
                0{index + 1}
              </span>
              <Icon className="mt-5 h-6 w-6 text-emerald-300" />
              <h3 className="mt-4 text-sm font-bold">{label}</h3>
              {index < WORKFLOW.length - 1 && (
                <ChevronRight className="absolute -right-3 top-1/2 hidden h-6 w-6 rounded-full bg-orange-500 p-1 text-white lg:block" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyMitra() {
  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.16em] text-blue-700">
            WHY MYSOCIETYMITRA
          </p>
          <h2 className="mt-4 font-display text-4xl font-black text-blue-950 sm:text-5xl">
            A better foundation for everyday Society work.
          </h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map(([title, body, color]) => (
            <article
              className="border border-slate-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl"
              key={title}
            >
              <span
                className={`flex h-11 w-11 items-center justify-center rounded-xl ${color === "blue" ? "bg-blue-100 text-blue-700" : color === "orange" ? "bg-orange-100 text-orange-700" : color === "green" ? "bg-emerald-100 text-emerald-700" : color === "violet" ? "bg-violet-100 text-violet-700" : "bg-rose-100 text-rose-700"}`}
              >
                <BadgeCheck className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-display text-xl font-bold text-slate-800">
                {title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                {body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section
      className="scroll-mt-24 bg-white py-20 sm:py-28"
      data-testid="premium-faq"
      id="faq"
    >
      <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[.8fr_1.2fr] lg:px-8">
        <div>
          <p className="text-xs font-bold tracking-[0.16em] text-orange-600">
            FAQ
          </p>
          <h2 className="mt-4 font-display text-4xl font-black text-blue-950 sm:text-5xl">
            Common questions about MySocietyMitra
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600">
            Quick answers about the platform, Ask Your Society Mitra, trials and
            the vendor and Expert networks.
          </p>
        </div>
        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {FAQS.map(([q, a], index) => (
            <div key={q}>
              <button
                aria-expanded={open === index}
                className="flex w-full items-center justify-between gap-4 py-5 text-left font-display text-lg font-bold text-slate-800"
                data-testid={`premium-faq-toggle-${index}`}
                onClick={() => setOpen(open === index ? -1 : index)}
                type="button"
              >
                {q}
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-orange-500 transition-transform duration-300 ${open === index ? "rotate-180" : ""}`}
                />
              </button>
              <p
                className={`overflow-hidden text-sm leading-relaxed text-slate-600 transition-[max-height,padding] duration-300 ${open === index ? "max-h-64 pb-5" : "max-h-0"}`}
              >
                {a}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onAsk, onExpert, onRegister, onVendor }) {
  return (
    <section className="px-5 py-16 sm:py-20">
      <div className="relative mx-auto max-w-7xl overflow-hidden bg-gradient-to-r from-blue-950 via-blue-900 to-indigo-900 px-6 py-14 text-center text-white sm:px-12 sm:py-20">
        <div className="absolute -left-16 top-0 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl" />
        <div className="absolute -right-16 bottom-0 h-64 w-64 rounded-full bg-orange-400/25 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-bold tracking-[0.12em] text-blue-100">
            <Sparkles className="h-4 w-4 text-orange-300" /> A SMARTER SOCIETY
            STARTS HERE
          </span>
          <h2 className="mt-6 font-display text-4xl font-black sm:text-5xl">
            Ready to Make Your Society Smarter?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-blue-100">
            Start your journey towards a more organized, connected and digitally
            managed Housing Society. {TRIAL_LABEL}.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              className="h-12 rounded-full bg-orange-500 px-6 text-white hover:bg-orange-600"
              data-testid="premium-final-register"
              onClick={onRegister}
              type="button"
            >
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              className="h-12 rounded-full border-white/30 bg-white/10 text-white hover:bg-white/20"
              data-testid="premium-final-ask"
              onClick={onAsk}
              type="button"
              variant="outline"
            >
              Ask Your Society Mitra
            </Button>
          </div>
          <div className="mt-5 flex flex-wrap justify-center gap-4 text-sm font-semibold text-blue-100">
            <button
              data-testid="premium-final-society"
              onClick={onRegister}
              type="button"
            >
              Join as Society
            </button>
            <button
              data-testid="premium-final-vendor"
              onClick={onVendor}
              type="button"
            >
              Join as Vendor
            </button>
            <button
              data-testid="premium-final-expert"
              onClick={onExpert}
              type="button"
            >
              Join as Expert
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function PremiumFooter({ onKnowledge, onLogin, onRegister }) {
  return (
    <footer
      className="bg-slate-950 text-slate-300"
      data-testid="premium-footer"
    >
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 md:grid-cols-[1.4fr_.75fr_.75fr] lg:px-8">
        <div>
          <img
            alt="MySocietyMitra by Shivkrupa Enterprises — Apka Society Mitra official logo"
            className="h-28 w-auto rounded-2xl bg-white p-1.5 object-contain sm:h-32"
            data-testid="premium-footer-logo"
            src="/mysocietymitra-official-logo.png"
          />
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
            MySocietyMitra by Shivkrupa Enterprises.{" "}
            <span className="text-orange-300">Apka Society Mitra.</span>
          </p>
          <div className="mt-5 space-y-2 text-sm">
            <a
              className="block transition-colors hover:text-white"
              data-testid="premium-footer-website"
              href={LANDING_CONTACT.websiteHref}
            >
              {LANDING_CONTACT.website}
            </a>
            <a
              className="block transition-colors hover:text-white"
              data-testid="premium-footer-email"
              href="mailto:info@mysocietymitra.com"
            >
              info@mysocietymitra.com
            </a>
            <div className="text-sm" data-testid="premium-footer-phones">
              {LANDING_CONTACT.phones.map((phone, index) => (
                <span key={phone.href}>
                  {index > 0 && <span className="px-1.5 text-slate-500">|</span>}
                  <a
                    className="transition-colors hover:text-white"
                    data-testid={`premium-footer-phone-${index + 1}`}
                    href={phone.href}
                  >
                    {phone.label}
                  </a>
                </span>
              ))}
            </div>
            <a
              className="block transition-colors hover:text-white"
              data-testid="premium-footer-whatsapp"
              href={LANDING_CONTACT.whatsappHref}
              rel="noreferrer"
              target="_blank"
            >
              WhatsApp: 8080-220-880
            </a>
          </div>
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-white">
            Platform
          </h3>
          <div className="mt-4 grid gap-3 text-sm">
            <button
              className="text-left hover:text-white"
              data-testid="premium-footer-knowledge"
              onClick={onKnowledge}
              type="button"
            >
              Knowledge
            </button>
            <button
              className="text-left hover:text-white"
              data-testid="premium-footer-login"
              onClick={onLogin}
              type="button"
            >
              Login
            </button>
            <button
              className="text-left hover:text-white"
              data-testid="premium-footer-onboarding"
              onClick={onRegister}
              type="button"
            >
              Onboarding
            </button>
          </div>
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-white">
            MySocietyMitra
          </h3>
          <div className="mt-4 grid gap-3 text-sm">
            <span>Ask Your Society Mitra</span>
            <span>Society Management</span>
            <span>Resident Portal</span>
            <span>Society Services</span>
            <span>Vendor Network</span>
            <span>Expert Network</span>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 px-5 py-5 text-center text-xs text-slate-500">
        © 2026 MySocietyMitra by Shivkrupa Enterprises. Built for Housing
        Societies.
      </div>
    </footer>
  );
}
